import { useState } from 'react'
import { Link, useForm } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Page, PageHeader, Section, Facts } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DocumentUploader } from '@/components/shared/DocumentUploader'
import { TemplateList } from '@/components/shared/TemplateList'
import { useBasePath } from '@/lib/paths'
import { BusinessTaskDetailed, DocumentAttachment, FieldDefinition, FieldValue } from '@/types'
import { FieldInputs, validateFields } from '@/components/applicant/FieldInputs'
import { FieldValues } from '@/components/applicant/FieldValues'

type Props = {
  task: BusinessTaskDetailed
  fields: FieldDefinition[]
  field_values: FieldValue[]
  documents: DocumentAttachment[]
  templates: DocumentAttachment[]
}

type ResponseValue = {
  field_id: string
  field_type: string
  value: string | number | string[] | null
  option_ids: string[]
}

function statusBadge(status: string): { label: string; variant: 'secondary' | 'default' } {
  if (status === 'completed') return { label: 'Complete', variant: 'default' }
  if (status === 'responded') return { label: 'With Corppy', variant: 'secondary' }
  return { label: 'Pending', variant: 'secondary' }
}

function formatDue(due_date: string | null, overdue: boolean): string {
  if (!due_date) return 'No due date'
  const date = new Date(due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return overdue ? `${date} (overdue)` : date
}

// FieldInputs tracks select and multi-select values by the option's id (not
// its `value` string), so the response carries those ids straight through.
function buildValues(
  fields: FieldDefinition[],
  fieldValues: Record<string, string | number | string[]>
): ResponseValue[] {
  return fields.map((field) => {
    const raw = fieldValues[field.id]
    if (field.field_type === 'select') {
      const id = typeof raw === 'string' ? raw : ''
      return { field_id: field.id, field_type: field.field_type, value: null, option_ids: id ? [id] : [] }
    }
    if (field.field_type === 'multi_select') {
      const ids = (Array.isArray(raw) ? raw : []).filter((id): id is string => Boolean(id))
      return { field_id: field.id, field_type: field.field_type, value: null, option_ids: ids }
    }
    return { field_id: field.id, field_type: field.field_type, value: raw ?? null, option_ids: [] }
  })
}

export default function BusinessTasksShow({ task, fields, field_values, documents, templates }: Props) {
  const basePath = useBasePath()
  const [fieldValues, setFieldValues] = useState<Record<string, string | number | string[]>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const form = useForm<{ values: ResponseValue[] }>({ values: [] })

  const readOnly = task.status !== 'pending'
  const badge = statusBadge(task.status)
  const businessHref = `${basePath}/businesses/${task.business_id}`
  const responsePath = `${basePath}/businesses/${task.business_id}/business_tasks/${task.id}/response`
  const documentsPath = `${basePath}/businesses/${task.business_id}/business_tasks/${task.id}/documents`

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const errors = validateFields(fields, fieldValues)
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }
    form.transform(() => ({ values: buildValues(fields, fieldValues) }))
    form.post(responsePath)
  }

  return (
    <DashboardLayout
      title={task.type_name}
      breadcrumbs={[
        { label: 'Businesses', href: 'businesses' },
        { label: task.business_name, href: `businesses/${task.business_id}` },
        { label: task.type_name },
      ]}
    >
      <Page>
        <PageHeader
          title={task.type_name}
          badge={<Badge variant={badge.variant}>{badge.label}</Badge>}
          subtitle={task.type_description}
        />

        <Facts
          items={[
            { label: 'Business', value: <Link href={businessHref} className="underline-offset-2 hover:underline">{task.business_name}</Link> },
            { label: 'Due date', value: formatDue(task.due_date, task.overdue) },
            { label: 'Status', value: badge.label },
          ]}
        />

        {task.instructions && (
          <Section title="Instructions" headingId="instructions-heading">
            <div className="whitespace-pre-wrap text-sm text-foreground">{task.instructions}</div>
          </Section>
        )}

        {fields.length > 0 && (
          <Section
            title={readOnly ? 'Your response' : 'Response'}
            headingId="response-heading"
          >
            {readOnly ? (
              <FieldValues fields={fields} values={field_values} />
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <FieldInputs
                  fields={fields}
                  values={fieldValues}
                  onChange={(fieldId, value) => setFieldValues((prev) => ({ ...prev, [fieldId]: value }))}
                  errors={fieldErrors}
                />
                <Button type="submit" disabled={form.processing}>
                  {form.processing ? 'Submitting…' : 'Submit response'}
                </Button>
              </form>
            )}
          </Section>
        )}

        <Section title="Documents" headingId="documents-heading">
          <div className="space-y-4">
            <TemplateList title="Document templates" templates={templates} />
            <DocumentUploader
              title="Documents"
              documents={documents}
              uploadPath={documentsPath}
              deletePath={(docId) => `${documentsPath}/${docId}`}
              readOnly={readOnly}
            />
          </div>
        </Section>
      </Page>
    </DashboardLayout>
  )
}
